import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useStudio } from '@/contexts/StudioContext';
import {
  DEFAULT_CALENDAR_START_HOUR,
  DEFAULT_CALENDAR_END_HOUR,
  getSwissHM,
  getSwissYMD,
  toLocalYMD,
  getDateRangeForView,
} from '@/components/appointments/calendarUtils';
import { CalendarLegend } from '@/components/appointments/CalendarLegend';
import { AppointmentsDialogs } from '@/components/appointments/AppointmentsDialogs';
import PhysioCheckinDialog from '@/components/appointments/PhysioCheckinDialog';
import SeriesManageDialog from '@/components/appointments/SeriesManageDialog';
import RescheduleDayDialog from '@/components/appointments/RescheduleDayDialog';
import WhatsAppRescheduleDialog from '@/components/appointments/WhatsAppRescheduleDialog';
import RescheduleEscalationsPanel from '@/components/appointments/RescheduleEscalationsPanel';
// 02/2026 Refactor Phase 2 — neue Render-Komponenten
import { BirthdayBanner } from '@/components/appointments/BirthdayBanner';
import { StaffTasksBanner } from '@/components/appointments/StaffTasksBanner';
import { StudioClosedBanner } from '@/components/appointments/StudioClosedBanner';
import { DragCursorFollower } from '@/components/appointments/DragCursorFollower';
import { CalendarHeader } from '@/components/appointments/CalendarHeader';
import { MonthView } from '@/components/appointments/MonthView';
import { WeekView } from '@/components/appointments/WeekView';
import { CurrentTimeIndicator } from '@/components/appointments/CurrentTimeIndicator';
import { DayViewGrid } from '@/components/appointments/DayViewGrid';
import { ResourceDayView } from '@/components/appointments/ResourceDayView';
import { DayListView } from '@/components/appointments/DayListView';
import { BlockEditDialog } from '@/components/appointments/BlockEditDialog';
import { useTrainerColumnReorder } from '@/hooks/useTrainerColumnReorder';
import { useAppointmentDragDrop } from '@/hooks/useAppointmentDragDrop';
import { useCalendarData } from '@/hooks/useCalendarData';
import { useBookingActions } from '@/hooks/useBookingActions';
import { useSlotClickActions } from '@/hooks/useSlotClickActions';
import { useAppointmentDialogs } from '@/hooks/useAppointmentDialogs';
import { useDeleteActions } from '@/hooks/useDeleteActions';
import { useBillingActions } from '@/hooks/useBillingActions';
import { runCustomerConflictCheck as runCustomerConflictCheckUtil } from '@/utils/customerConflictCheck';
// 06/2026 Refactor Phase 1 — Extraktion in eigene Files (siehe finish summary)
// Iter 333: Nach Phase 5 verbleiben hier nur noch fetchStudiosApi (Mount-only).
// Die anderen Service-Calls wurden in die Hooks ausgelagert.
import { fetchStudiosApi } from '@/services/appointmentsApi';
import SeriesSyncProgress from '@/components/appointments/SeriesSyncProgress';
import {
  getWeekStart,
  getWeekDays as utilGetWeekDays,
  getDaysInMonth as utilGetDaysInMonth,
  formatHeaderDate,
  advanceDate,
  isStudioOpenOnDate as utilIsStudioOpenOnDate,
} from '@/utils/calendarDateUtils';
import {
  getAppointmentColor as utilGetAppointmentColor,
  getPaymentStatusColor as utilGetPaymentStatusColor,
  getPaymentBadge as utilGetPaymentBadge,
  calculateAppointmentHeight as utilCalculateAppointmentHeight,
  isFirstSlotOfAppointment as utilIsFirstSlotOfAppointment,
} from '@/utils/appointmentDisplay';
import {
  getValidSlotStartsForTrainer as utilGetValidSlotStartsForTrainer,
  findTrainerVacation as utilFindTrainerVacation,
  getAppointmentForSlot as utilGetAppointmentForSlot,
  isTrainerAvailable as utilIsTrainerAvailable,
  isSlotOpenedByException as utilIsSlotOpenedByException,
  getSlotBlockReason as utilGetSlotBlockReason,
  getAdminOnlySlotInfo as utilGetAdminOnlySlotInfo,
} from '@/utils/trainerSlotLogic';
import {
  computeAdjustedSlot,
  computeDropTimes,
  isMindbodyOnlyAppointment,
  startEdgeAutoScroll,
  computeOccupancy,
  getMaxParticipants,
} from '@/utils/calendarDragDrop';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppointmentsPage = () => {
  const [view, setView] = useState('day'); // day, week, month
  // 06/2026 (Stage 2 Beauty): Kalender-Modus — Mitarbeiter-Spalten vs. Platz-Spalten
  const [calendarMode, setCalendarMode] = useState('trainer'); // 'trainer' | 'resource'
  // 06/2026 (Spezifikation): Zwei Kalenderansichten — Raster (Grid) vs.
  // Tagesliste. Auswahl wird pro Benutzer serverseitig gemerkt.
  const [dayLayout, setDayLayout] = useState('raster'); // 'raster' | 'liste'
  // Mandanten-Konfiguration des Rechtsklick-Menüs (/settings/calendar-menu).
  const [menuConfig, setMenuConfig] = useState(null);
  const [resources, setResources] = useState([]);
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Initialize currentDate from URL parameter or default to today
  const getInitialDate = () => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const parsedDate = new Date(dateParam);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }
    return new Date();
  };
  
  const [currentDate, setCurrentDate] = useState(getInitialDate);
  // Iter 318 (Feb 2026): Trainer-Spalten-Reihenfolge pro Studio – Drag&Drop
  // im Kalender-Header. Persistiert via /api/trainers/order.
  // Logik ausgelagert in useTrainerColumnReorder (02/2026 Refactor).
  // `trainers`-State lebt jetzt in useCalendarData (Phase 3, Iter 331).
  // Multi-Studio (P0 / Iter 287 Fix): Studio-Filter ist jetzt mit dem
  // globalen `StudioContext` (Header-Switcher) verbunden. Wenn der User
  // oben "Rapperswil" wählt, filtert der Kalender automatisch mit. Klickt
  // er den Kalender-Dropdown, wird der globale Switcher mit synchronisiert.
  const { selectedStudio: globalStudio, selectStudio: setGlobalStudio } = useStudio();
  const [studios, setStudios] = useState([]);
  // Iter 287: „Alle Studios" entfernt — Kalender zeigt immer genau ein Studio.
  // Fallback: erstes Studio in der Liste falls Context noch nichts hat.
  const studioFilter = (globalStudio && globalStudio.id && !globalStudio.is_all)
    ? globalStudio.id
    : (studios[0]?.id || 'all');
  const setStudioFilter = (val) => {
    const s = studios.find(x => x.id === val);
    if (s) setGlobalStudio(s);
  };
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchStudiosApi();
        setStudios(data);
      } catch {
        // Studios konnten nicht geladen werden – Fallback im UI greift.
      }
    })();
  }, []);

  // 06/2026 (Stage 2 Beauty): Ressourcen/Plätze laden (für Platz-Ansicht).
  // Mandanten-scoped über das Token; kein Studio-Filter, damit die Ansicht
  // unabhängig von der aktuellen Studio-Auswahl funktioniert.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/resources`);
        setResources(Array.isArray(res.data) ? res.data : []);
      } catch {
        setResources([]);
      }
    })();
  }, []);

  // ──────────────────────────────────────────────────────────────────
  //  CALENDAR DATA (Phase 3, Iter 331): alle Daten-Fetches + State
  //  liegen jetzt im useCalendarData-Hook.
  // ──────────────────────────────────────────────────────────────────
  const [billingInProgress, setBillingInProgress] = useState(false);

  // Pause/Blocker eintragen — Formular-Logik in components/appointments/BreakDialog.jsx
  const [breakDialogOpen, setBreakDialogOpen] = useState(false);
  // Tag verschieben (08/2026)
  const [rescheduleDayOpen, setRescheduleDayOpen] = useState(false);
  const [waRescheduleAppt, setWaRescheduleAppt] = useState(null);
  const [escalationRefresh, setEscalationRefresh] = useState(0);
  // Modal-Ersatz für unleserlichen Toast bei Serien-Buchungen (06/2026)
  const [seriesSkippedInfo, setSeriesSkippedInfo] = useState(null);

  const [showAllTrainers, setShowAllTrainersState] = useState(() => localStorage.getItem('calendar_show_all_trainers') !== '0');
  const setShowAllTrainers = useCallback((val) => {
    setShowAllTrainersState(prev => {
      const next = typeof val === 'function' ? val(prev) : val;
      localStorage.setItem('calendar_show_all_trainers', next ? '1' : '0');
      return next;
    });
  }, []);
  const [slotHeight, setSlotHeight] = useState(60); // Default 60px (standard for calendars)
  // 06/2026 Drop-Präzision: Ref auf den Kalender-Body für cursor-basierte Drops
  const calendarGridRef = useRef(null);
  // NEW: appointment to auto-open/highlight (from ?selected=...)
  const [autoSelectedAppointmentId, setAutoSelectedAppointmentId] = useState(() => searchParams.get('selected'));

  // Booking Dialog State
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [isAddingParticipant, setIsAddingParticipant] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [customTime, setCustomTime] = useState(''); // For custom time input
  const [bookingData, setBookingData] = useState({
    service_id: '',
    customer_id: '',
    notes: ''
  });
  const [isBooking, setIsBooking] = useState(false); // NEU: Verhindert Doppelklick
  
  // Recurring Appointment State
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState('weekly');
  const [recurringOccurrences, setRecurringOccurrences] = useState(10);

  // Kunden-Benachrichtigung (E-Mail & WhatsApp) beim Buchen — Standard AN.
  // Wahl bleibt über localStorage erhalten (z.B. bei vielen Umbuchungen aus).
  const [notifyCustomer, setNotifyCustomerState] = useState(() => {
    try { return localStorage.getItem('sf_notify_customer') !== '0'; } catch { return true; }
  });
  const setNotifyCustomer = (v) => {
    setNotifyCustomerState(v);
    try { localStorage.setItem('sf_notify_customer', v ? '1' : '0'); } catch { /* noop */ }
  };
  
  // Context Menu & Edit State
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [movingAppointment, setMovingAppointment] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteOptionsDialogOpen, setDeleteOptionsDialogOpen] = useState(false); // NEW: For delete options
  const [manageSeriesId, setManageSeriesId] = useState(null); // 08/2026: Serien-Verwaltung
  const [overwriteWarningDialogOpen, setOverwriteWarningDialogOpen] = useState(false); // NEW: For overwrite warning
  const [conflictingDates, setConflictingDates] = useState([]); // NEW: Store conflicting dates
  const [pendingBookingData, setPendingBookingData] = useState(null); // NEW: Store booking data for confirmation
  
  // Quick Checkout Dialog State
  const [quickCheckoutOpen, setQuickCheckoutOpen] = useState(false);
  
  // Availability Management State (for blocking/unblocking slots)
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [availabilitySlot, setAvailabilitySlot] = useState(null); // {trainer, timeSlot, isAvailable}
  const [checkoutCustomer, setCheckoutCustomer] = useState(null);
  // Physio-Abrechnung: Dialog nach Check-in eines Insurance-Patienten
  const [physioCheckin, setPhysioCheckin] = useState({ open: false, customerId: null, data: null });

  // Anamnese-Dialog (Anamnese-Status selber lebt in useCalendarData)
  const [anamneseDialogOpen, setAnamneseDialogOpen] = useState(false);
  const [anamneseCustomer, setAnamneseCustomer] = useState(null);

  // 08/2026 (Sonja-Fall): "Versteckte" Termine des Tages (fehlender/unbekannter
  // Trainer) erkennen und als Warnbanner mit Reparatur-Button anzeigen.
  const [hiddenDayIssues, setHiddenDayIssues] = useState([]);
  useEffect(() => {
    const d = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
    axios.get(`${API}/appointments/day-audit?date=${d}`)
      .then((res) => setHiddenDayIssues(res.data.issues || []))
      .catch(() => setHiddenDayIssues([]));
  }, [currentDate]);

  // 08/2026 Phase 2: Kalender aktualisieren, wenn ein Serien-Hintergrund-Job
  // (Buchen/Löschen) fertig ist. (Listener wird weiter unten registriert, nachdem
  // fetchAppointments initialisiert ist — sonst TDZ/White-Screen.)


  const openEditForAppointment = useCallback((found) => {
    setSelectedAppointment(found);
    const aptStartDate = new Date(found.start_time);
    const { hour: _eh, minute: _em } = getSwissHM(found.start_time);
    setCustomTime(`${String(_eh).padStart(2, '0')}:${String(_em).padStart(2, '0')}`);
    let _dur = Number(found.duration) || null;
    if (!_dur && found.end_time) {
      const _diff = Math.round((new Date(found.end_time) - aptStartDate) / 60000);
      if (_diff > 0) _dur = _diff;
    }
    setBookingData({
      service_id: found.service_id,
      customer_id: found.customer_ids?.[0] || found.customer_id,
      notes: found.notes || '',
      duration_override: _dur || ''
    });
    setEditDialogOpen(true);
  }, []);

  // Auto-Select-Callback nach jedem fetchAppointments: prüft ob die Page
  // via ?selected=<id> aufgerufen wurde und öffnet ggf. den Edit-Dialog.
  // Wird in useCalendarData injiziert.
  const handleAutoSelectAppointment = useCallback((appts) => {
    if (!autoSelectedAppointmentId) return;
    const realAppts = appts.filter((a) => !a._isReservation);
    const found = realAppts.find((a) => a.id === autoSelectedAppointmentId);
    if (found) {
      setSelectedAppointment(found);
      // Prefill wie in handleEditAppointment — ohne das öffnete das
      // Modal mit LEEREN Feldern (Bug 06/2026: Klick aus Historie).
      const aptStartDate = new Date(found.start_time);
      const { hour: _eh, minute: _em } = getSwissHM(found.start_time);
      setCustomTime(`${String(_eh).padStart(2, '0')}:${String(_em).padStart(2, '0')}`);
      let _dur = Number(found.duration) || null;
      if (!_dur && found.end_time) {
        const _diff = Math.round((new Date(found.end_time) - aptStartDate) / 60000);
        if (_diff > 0) _dur = _diff;
      }
      setBookingData({
        service_id: found.service_id,
        customer_id: found.customer_ids?.[0] || found.customer_id,
        notes: found.notes || '',
        duration_override: _dur || ''
      });
      setEditDialogOpen(true);
    }
    setAutoSelectedAppointmentId(null);
  }, [autoSelectedAppointmentId]);

  const {
    calendarStartHour,
    calendarEndHour,
    studioOpeningHours,
    densityConfig,
    isStudioOpenOnDate,
    trainers,
    appointments,
    setAppointments,
    showCancelled,
    setShowCancelled,
    mindbodySlots,
    services,
    availableServices,
    availableServicesInfo,
    setAvailableServices,
    customers,
    anamneseStatus,
    birthdaysToday,
    paymentColors,
    trainerVacations,
    trainerBlocks,
    trainerExceptions,
    loading,
    mbSyncing,
    lastFetchedAt,
    fetchTrainers,
    fetchAppointments,
    fetchServices,
    fetchCustomers,
    fetchAnamneseStatus,
    fetchVacationsAndBlocks,
    fetchAvailableServices,
    handleMindbodySync,
  } = useCalendarData({
    currentDate,
    view,
    showAllTrainers,
    onAutoSelectAppointment: handleAutoSelectAppointment,
  });

  // 06/2026 (Spezifikation): gespeicherte Ansicht pro Benutzer + Mandanten-
  // Kontextmenü laden.
  useEffect(() => {
    (async () => {
      try {
        const [pref, menu] = await Promise.all([
          axios.get(`${API}/me/calendar-view`),
          axios.get(`${API}/settings/calendar-menu`),
        ]);
        if (pref.data?.view_layout) setDayLayout(pref.data.view_layout);
        if (Array.isArray(menu.data?.entries)) setMenuConfig(menu.data.entries);
      } catch {
        // Defaults (Raster + volles Menü) greifen.
      }
    })();
  }, []);

  const changeDayLayout = useCallback((layout) => {
    setDayLayout(layout);
    axios.put(`${API}/me/calendar-view`, { view_layout: layout }).catch(() => {});
  }, []);

  // Schnell-Check-in aus der Tagesliste (ohne Umweg über das Kontextmenü).
  const quickCheckIn = useCallback(async (apt) => {
    if (apt.checked_in) { toast.info('Dieser Termin wurde bereits eingecheckt.'); return; }
    try {
      await axios.post(`${API}/appointments/${apt.id}/check-in`, {});
      toast.success('Check-In erfolgreich');
      fetchAppointments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Check-In fehlgeschlagen');
    }
  }, [fetchAppointments]);

  // 06/2026: Studioweite Sperren (studio_blocks) für den aktuellen Tag laden.
  const [studioBlocks, setStudioBlocks] = useState([]);
  // 06/2026: Sperre bearbeiten (Grund ändern / aufheben)
  const [editingBlock, setEditingBlock] = useState(null);
  const fetchStudioBlocks = useCallback(async () => {
    if (!studioFilter || studioFilter === 'all') {
      setStudioBlocks([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/studios/${studioFilter}/blocks`, {
        params: { date: toLocalYMD(currentDate) },
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudioBlocks(Array.isArray(res.data) ? res.data : []);
    } catch {
      setStudioBlocks([]);
    }
  }, [studioFilter, currentDate]);
  useEffect(() => {
    fetchStudioBlocks();
  }, [fetchStudioBlocks]);

  // Prüft, ob ein Slot durch eine studioweite Sperre betroffen ist (für die
  // Grid-Markierung UND die Klick-Erkennung).
  const findStudioBlockForSlot = useCallback(
    (trainerData, timeSlot) => {
      if (!studioBlocks.length) return null;
      const dateStr = toLocalYMD(currentDate);
      const [h, m] = timeSlot.split(':').map(Number);
      const t = h * 60 + m;
      const tStudios = [
        ...((trainerData?.trainer?.studio_ids) || trainerData?.studio_ids || []),
        trainerData?.trainer?.studio_id || trainerData?.studio_id,
      ].filter(Boolean);
      return (
        studioBlocks.find((b) => {
          if (b.date !== dateStr) return false;
          const applies =
            (studioFilter && studioFilter !== 'all' && b.studio_id === studioFilter) ||
            tStudios.includes(b.studio_id);
          if (!applies) return false;
          if (b.all_day) return true;
          const [sH, sM] = (b.start_time || '00:00').split(':').map(Number);
          const [eH, eM] = (b.end_time || '23:59').split(':').map(Number);
          return t >= sH * 60 + sM && t < eH * 60 + eM;
        }) || null
      );
    },
    [studioBlocks, currentDate, studioFilter]
  );


  const serviceMap = useMemo(() => {
    return new Map(services.map(service => [service.id, service]));
  }, [services]);

  const appointmentsByDateString = useMemo(() => {
    const map = {};
    appointments.forEach((apt) => {
      const key = new Date(apt.start_time).toDateString();
      if (!map[key]) {
        map[key] = [];
      }
      map[key].push(apt);
    });
    return map;
  }, [appointments]);

  const appointmentCountsByIsoDate = useMemo(() => {
    const counts = {};
    appointments.forEach((apt) => {
      const key = getSwissYMD(apt.start_time);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [appointments]);

  // Multi-Studio Filter: filtere appointments + trainers nach gewähltem Studio
  // (oder zeige alles bei 'all').
  //
  // Iter 336.e: Sicherheitsnetz für „verwaiste" Termine — wenn ein Termin
  // eine `studio_id` hat, die NICHT in der aktiven `studios`-Liste existiert
  // (Studio wurde nachträglich gelöscht oder ist inaktiv geworden), wäre der
  // Termin in JEDEM Studio-Filter unsichtbar. Wir zeigen solche Termine im
  // aktuell gewählten Filter mit an, damit der Admin sie sehen und neu
  // zuordnen kann (zusätzlich Banner unten).
  const orphanStudioAppointments = useMemo(() => {
    const knownStudioIds = new Set(studios.map((s) => s.id));
    return appointments.filter(
      (apt) => apt.studio_id && !knownStudioIds.has(apt.studio_id)
    );
  }, [appointments, studios]);

  const filteredAppointments = useMemo(() => {
    if (studioFilter === 'all') return appointments;
    const knownStudioIds = new Set(studios.map((s) => s.id));
    return appointments.filter((apt) => {
      if (apt.studio_id === studioFilter) return true;
      // Verwaiste Termine (gelöschtes/unbekanntes Studio) → in jedem Filter sichtbar
      if (apt.studio_id && !knownStudioIds.has(apt.studio_id)) return true;
      // Termine ohne studio_id (Legacy / fehlerhafte Buchung) → in jedem Filter sichtbar
      if (!apt.studio_id) return true;
      return false;
    });
  }, [appointments, studioFilter, studios]);

  const filteredTrainers = useMemo(() => {
    if (studioFilter === 'all') return trainers;
    // Iter 312 Bugfix (Feb 2026): Studio-Zuordnung eines Trainers wird
    // primär aus den Wochen-Slots `availability[day].slots[].studio_id`
    // abgeleitet — NICHT mehr ausschliesslich aus dem Legacy-Feld
    // `users.studio_ids`. Grund: Der User pflegt Studios nur in den
    // Verfügbarkeits-Slots ("Personal-Profil" → Wochentage), nicht im
    // globalen User-Record. Ohne diesen Fix erscheint ein Trainer mit
    // `studio_ids=[]` in ALLEN Studios (Fallback line) — und damit auch
    // in Rapperswil, obwohl er nur Wetzikon-Slots hat.
    //
    // Day-View: prüfe NUR den aktuell selektierten Wochentag — sonst
    // würde ein Trainer mit Wed=Rapperswil + Tue=Wetzikon auch am
    // Dienstag in der Rapperswil-Spalte auftauchen.
    // Week/Month-View: prüfe die gesamte Woche (Union).
    const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
    const currentDayName = currentDate
      .toLocaleDateString('en-US', { weekday: 'long' })
      .toLowerCase();
    return trainers.filter((td) => {
      const trainerData = td.trainer || td;
      const availability = td.availability || {};

      const collectStudios = (dayKey) => {
        const dayCfg = availability[dayKey];
        if (!dayCfg || !dayCfg.active) return [];
        const slots = dayCfg.slots || [];
        return slots
          .map((s) => s.studio_id)
          .filter((sid) => !!sid);
      };

      // Legacy: explizite Studio-Zuordnung im User-Profil (nur Fallback)
      const profileStudios = trainerData.studio_ids || [];

      // Arbeitsplan ist MASSGEBLICH: Hat der Trainer irgendwo in seiner
      // Wochenkonfig Studio-Slots, filtern wir AUSSCHLIESSLICH danach und
      // ignorieren die statische Profil-Zuordnung. Sonst erscheint ein
      // Trainer mit Profil=[Wetzikon,Rappi], der aber nur Wetzikon-Slots
      // hat, fälschlich auch in Rapperswil (Bug Okt 2026).
      const scheduleStudiosWeek = new Set(DAYS.flatMap(collectStudios));
      const hasScheduleStudios = scheduleStudiosWeek.size > 0;

      let visibleStudios;
      if (hasScheduleStudios) {
        visibleStudios =
          view === 'day'
            ? new Set(collectStudios(currentDayName))
            : scheduleStudiosWeek;
      } else if (profileStudios.length > 0) {
        // Kein Arbeitsplan hinterlegt → Legacy-Profilzuordnung
        visibleStudios = new Set(profileStudios);
      } else {
        // Gar keine Zuordnung → in allen Studios sichtbar (Fresh-Trainer)
        return true;
      }
      return visibleStudios.has(studioFilter);
    });
  }, [trainers, studioFilter, view, currentDate]);

  // Iter 318: Trainer-Spalten-Reihenfolge + DnD-Header ausgelagert in
  // useTrainerColumnReorder (02/2026 Refactor).
  const {
    orderedFilteredTrainers,
    draggingTrainerId,
    setDraggingTrainerId,
    dragOverTrainerId,
    setDragOverTrainerId,
    handleTrainerColumnDrop,
  } = useTrainerColumnReorder({ studioFilter, filteredTrainers });

  const appointmentsByTrainerForDate = useMemo(() => {
    const map = {};
    const dateKey = currentDate.toDateString();
    filteredAppointments.forEach((apt) => {
      if (!apt.trainer_id) return;
      const aptDateKey = new Date(apt.start_time).toDateString();
      if (aptDateKey !== dateKey) return;
      if (!map[apt.trainer_id]) {
        map[apt.trainer_id] = [];
      }
      map[apt.trainer_id].push(apt);
    });
    return map;
  }, [filteredAppointments, currentDate]);

  // Iter 318 (Feb 2026): Kleinste Service-Gesamtdauer (inkl. Buffers
  // & Warmup). Wird zum Ausblenden von "+"-Buttons in zu kleinen
  // Lücken genutzt. Falls noch keine Services geladen sind →
  // 0 (= "+" bleibt überall sichtbar, Dialog filtert dann nach Klick).
  const minServiceTotalDuration = useMemo(() => {
    if (!services || services.length === 0) return 0;
    const totals = services
      .filter((s) => s && s.is_active !== false)
      .map((s) => (
        // 07/2026: Warmup nicht mehr im Slot-Filter berücksichtigen —
        // ist nur für Add-on-Puffer-Logik relevant.
        (Number(s.duration_minutes) || 0)
        + (Number(s.buffer_time_before) || 0)
        + (Number(s.buffer_time_after) || 0)
      ))
      .filter((n) => n > 0);
    return totals.length ? Math.min(...totals) : 0;
  }, [services]);

  // Liefert die maximale Lücken-Dauer (in Minuten) ab `slotStart` auf
  // der gegebenen Trainer-Spalte, bevor der nächste aktive Termin
  // beginnt. Wird verwendet, um zu kleine "+"-Slots auszublenden.
  const getMaxGapMinutes = useCallback((trainerId, slotStartDate) => {
    const apts = appointmentsByTrainerForDate[trainerId] || [];
    const slotMs = slotStartDate.getTime();
    let nextStartMs = Infinity;
    apts.forEach((a) => {
      if (!a || !a.start_time) return;
      if (a.status === 'cancelled' || a.status === 'deleted') return;
      const startMs = new Date(a.start_time).getTime();
      if (startMs > slotMs && startMs < nextStartMs) nextStartMs = startMs;
    });
    if (nextStartMs === Infinity) return 24 * 60;
    return Math.max(0, Math.floor((nextStartMs - slotMs) / 60000));
  }, [appointmentsByTrainerForDate]);

  // Mindbody-Slots (BELEGT) für aktuelles Datum — werden NUR auf der
  // zugeordneten Trainer-Spalte als grauer Read-Only-Block angezeigt.
  // Slots ohne Mapping (strongfit_trainer_id = null) werden ausgeblendet,
  // da nur explizit zugeordnete Mitarbeiter im Kalender erscheinen sollen.
  // Multi-Studio (Iter 287): Mindbody-Slots gehören zu EINEM externen
  // Standort (typischerweise Wetzikon). Wenn der Admin auf Rapperswil
  // wechselt, werden Mindbody-Slots ausgeblendet (sie blockieren das
  // andere Studio nicht).
  const mindbodySlotsByTrainer = useMemo(() => {
    const dateKey = currentDate.toDateString();
    const map = {};
    mindbodySlots.forEach((s) => {
      if (!s.strongfit_trainer_id) return;
      // Studio-Filter: Mindbody-Slots zeigen nur in ihrem zugeordneten
      // Studio (oder im „Alle Studios"-Modus).
      if (studioFilter !== 'all' && s.studio_id && s.studio_id !== studioFilter) return;
      try {
        if (new Date(s.start_time).toDateString() !== dateKey) return;
      } catch {
        return;
      }
      if (!map[s.strongfit_trainer_id]) map[s.strongfit_trainer_id] = [];
      map[s.strongfit_trainer_id].push(s);
    });
    return map;
  }, [mindbodySlots, currentDate, studioFilter]);

  // Iter 324: Sucht den Mindbody-BELEGT-Slot, der den geklickten Zeitpunkt
  // (HH:MM in Swiss-TZ) auf dieser Trainer-Spalte überlappt — sofern er ein
  // gemapptes StrongFit-Service hat. Wird genutzt, um beim Buchen neben
  // einem BELEGT-Mindbody-Slot das passende Service vorzuwählen.
  const findMindbodyMappedSlotAt = useCallback((trainerId, clickedHHMM) => {
    const slots = mindbodySlotsByTrainer[trainerId] || [];
    if (!slots.length || !clickedHHMM) return null;
    const [ch, cm] = String(clickedHHMM).split(':').map(Number);
    if (Number.isNaN(ch) || Number.isNaN(cm)) return null;
    const clickedMins = ch * 60 + cm;
    for (const mb of slots) {
      if (!mb.strongfit_service_id) continue;
      if (mb.source_type !== 'appointment') continue; // Pausen ignorieren
      try {
        const sHM = getSwissHM(mb.start_time);
        const eHM = getSwissHM(mb.end_time);
        const sMins = sHM.hour * 60 + sHM.minute;
        const eMins = eHM.hour * 60 + eHM.minute;
        if (clickedMins >= sMins && clickedMins < eMins) return mb;
      } catch {
        /* skip */
      }
    }
    return null;
  }, [mindbodySlotsByTrainer]);
  
  // Vacation/Block UI State (Daten kommen aus useCalendarData)
  const [blockDialogOpen, setBlockDialogOpen] = useState(false); // NEW
  const [blockFormData, setBlockFormData] = useState({ // NEW
    start_time: '',
    end_time: '',
    reason: ''
  });

  // Drag & Drop Hook (Phase 3, Iter 331). Stellt sowohl State als auch
  // Handler bereit.
  const {
    draggedAppointment,
    setDraggedAppointment,
    draggedParticipant,
    setDraggedParticipant,
    dropTarget,
    setDropTarget,
    dragCursorPos,
    handleDragStart,
    handleParticipantDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    getSlotBlockReason,
  } = useAppointmentDragDrop({
    appointments,
    mindbodySlotsByTrainer,
    services,
    customers,
    currentDate,
    fetchAppointments,
    gridRef: calendarGridRef,
    slotHeight,
    calendarStartHour,
    calendarEndHour,
  });

  // Generiere Zeitslots basierend auf Start- und Endzeit (mit useMemo für Performance)
  // 06/2026 Slot-Packing-Fix: 15-Min-Raster statt 30-Min, damit verankerte
  // Verfügbarkeiten (z.B. 17:45) jemals gerendert werden können.
  // Visuell zeigen wir Labels nur an :00 / :30, alle anderen Zeilen sind halbhoch.
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = calendarStartHour; hour < calendarEndHour; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:15`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
      slots.push(`${hour.toString().padStart(2, '0')}:45`);
    }
    return slots;
  }, [calendarStartHour, calendarEndHour]);

  // Berechne Startminuten für Positionierung (mit useMemo damit es sich aktualisiert)
  const CALENDAR_START_MINUTES = useMemo(() => calendarStartHour * 60, [calendarStartHour]);

  // Handle view anamnese for a customer
  const handleViewAnamnese = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setAnamneseCustomer(customer);
      setAnamneseDialogOpen(true);
    }
    setContextMenu(null);
  };

  // Slot-Klick-Handler — Phase 4 (Iter 332) ausgelagert in useSlotClickActions
  const {
    handleSlotClick,
    handleUnavailableSlotClick,
    handleAvailableSlotClick,
    handleAddException,
    handleAddBlock,
    handleAddBlockAllDay,
    handleAddStudioBlock,
    handleRemoveStudioBlock,
    handleRemoveBlock,
    handleRemoveBlockException,
  } = useSlotClickActions({
    services,
    customers,
    currentDate,
    trainerExceptions,
    trainerBlocks,
    studioBlocks,
    studioFilter,
    minServiceTotalDuration,
    getMaxGapMinutes,
    findMindbodyMappedSlotAt,
    setSelectedSlot,
    setSelectedTrainer,
    setBookingData,
    setCustomTime,
    setBookingDialogOpen,
    setAvailabilitySlot,
    setAvailabilityDialogOpen,
    availabilitySlot,
    fetchServices,
    fetchCustomers,
    fetchAvailableServices,
    fetchTrainers,
    fetchVacationsAndBlocks,
    fetchStudioBlocks,
  });

  // Booking/Edit Action Handler — Phase 3 (Iter 331) ausgelagert
  // in useBookingActions (Hook wird nach handleEditSubmit referenziert).


  // ──────────────────────────────────────────────────────────────────
  //  Dialog-Aktionen (Phase 5, Iter 333) — Kontext-Menü + Edit + CheckIn
  // ──────────────────────────────────────────────────────────────────
  const {
    handleRightClick,
    handleEditAppointment,
    handleCheckIn,
    handleQuickCheckoutSuccess,
    handleMoveAppointment,
    handleMoveParticipant,
  } = useAppointmentDialogs({
    selectedAppointment,
    setSelectedAppointment,
    setContextMenu,
    setMovingAppointment,
    trainers,
    setSelectedTrainer,
    setCustomTime,
    setBookingData,
    setEditDialogOpen,
    services,
    fetchServices,
    setCheckoutCustomer,
    setQuickCheckoutOpen,
    setPhysioCheckin,
    fetchAppointments,
  });

  // ──────────────────────────────────────────────────────────────────────
  //  KUNDEN-KONFLIKT-PREFLIGHT
  // ──────────────────────────────────────────────────────────────────────
  // Prüft via Backend ob der Kunde am Vortag/selben Tag/Folgetag bereits
  // einen aktiven Termin hat. Logik liegt in utils/customerConflictCheck;
  // hier wird `customers` injiziert, damit der Confirm-Dialog den Namen
  // anzeigen kann.
  const runCustomerConflictCheck = (customerId, startTimeIso, excludeAppointmentId = null) =>
    runCustomerConflictCheckUtil(customerId, startTimeIso, excludeAppointmentId, customers);

  // ──────────────────────────────────────────────────────────────────
  //  Delete-Aktionen (Phase 5, Iter 333)
  // ──────────────────────────────────────────────────────────────────
  const {
    handleDeleteAppointment,
    confirmDelete,
    handleRemoveParticipant,
  } = useDeleteActions({
    appointments,
    setAppointments,
    selectedAppointment,
    setSelectedAppointment,
    setContextMenu,
    setDeleteDialogOpen,
    setDeleteOptionsDialogOpen,
    fetchAppointments,
  });

  // 08/2026 Phase 2: Kalender aktualisieren, wenn ein Serien-Hintergrund-Job
  // (Buchen/Löschen) fertig ist. Hier registriert, NACHDEM fetchAppointments existiert.
  useEffect(() => {
    const onSeriesDone = () => { try { fetchAppointments(); } catch (_) {} };
    window.addEventListener('series-job-done', onSeriesDone);
    // Sprung zum Datum eines Verlaufs-Eintrags
    const onJump = (e) => {
      const iso = e?.detail?.date;
      if (!iso) return;
      const d = new Date(iso);
      if (!isNaN(d.getTime())) setCurrentDate(d);
    };
    window.addEventListener('jump-to-calendar-date', onJump);
    return () => {
      window.removeEventListener('series-job-done', onSeriesDone);
      window.removeEventListener('jump-to-calendar-date', onJump);
    };
  }, [fetchAppointments]);


  // 08/2026: Wenn der Admin im Buchungs-Dialog die Startzeit ändert,
  // Service-Liste vom Backend neu laden (vorher blieb sie veraltet und
  // zeigte Services, die zur neuen Zeit ggf. nicht passen — oder blieb leer).
  const _lastDialogTimeRef = useRef(null);
  useEffect(() => {
    if (!bookingDialogOpen) {
      _lastDialogTimeRef.current = null;
      return;
    }
    if (!/^\d{2}:\d{2}$/.test(customTime || '')) return;
    if (_lastDialogTimeRef.current === null) {
      // Initiales Öffnen — Services wurden bereits beim Slot-Klick geladen
      _lastDialogTimeRef.current = customTime;
      return;
    }
    if (_lastDialogTimeRef.current === customTime) return;
    _lastDialogTimeRef.current = customTime;
    const _trainerId = selectedTrainer?.trainer?.id || selectedTrainer?.id;
    if (!_trainerId) return;
    fetchAvailableServices(
      _trainerId,
      toLocalYMD(currentDate),
      customTime
    );
  }, [customTime, bookingDialogOpen, selectedTrainer, currentDate, fetchAvailableServices]);

  // Booking-Hook (Phase 3, Iter 331) — kapselt handleBookingSubmit,
  // confirmBookingWithConflicts und handleEditSubmit inkl. aller Pre-Checks.
  const {
    handleBookingSubmit,
    confirmBookingWithConflicts,
    handleEditSubmit,
    seriesSyncId,
    setSeriesSyncId,
  } = useBookingActions({
    user,
    bookingData,
    customTime,
    setCustomTime,
    selectedSlot,
    selectedTrainer,
    selectedAppointment,
    setSelectedAppointment,
    isRecurring,
    setIsRecurring,
    recurringFrequency,
    setRecurringFrequency,
    recurringOccurrences,
    setRecurringOccurrences,
    notifyCustomer,
    isBooking,
    setIsBooking,
    setBookingDialogOpen,
    setIsAddingParticipant,
    setEditDialogOpen,
    setOverwriteWarningDialogOpen,
    pendingBookingData,
    setPendingBookingData,
    setConflictingDates,
    setSeriesSkippedInfo,
    appointments,
    customers,
    services,
    currentDate,
    studioFilter,
    fetchAppointments,
  });

  // ──────────────────────────────────────────────────────────────────
  //  Billing & Move-To-Slot (Phase 5, Iter 333)
  // ──────────────────────────────────────────────────────────────────
  const { handleManualBilling, handleMoveToSlot } = useBillingActions({
    billingInProgress,
    setBillingInProgress,
    movingAppointment,
    setMovingAppointment,
    services,
    currentDate,
    customers,
    fetchAppointments,
  });

  const navigateDate = (direction) => {
    setCurrentDate(advanceDate(currentDate, view, direction));
  };

  const formatDate = () => formatHeaderDate(currentDate, view);

  const getWeekDays = () => utilGetWeekDays(currentDate);

  const getDaysInMonth = () => utilGetDaysInMonth(currentDate);

  const getTimeSlots = () => timeSlots;

  const getAppointmentColor = (appointment) =>
    utilGetAppointmentColor(appointment, services, paymentColors);

  const getPaymentStatusColor = utilGetPaymentStatusColor;

  const getPaymentBadge = utilGetPaymentBadge;

  // 06/2026 Slot-Packing-Logik: Berechnet die VALIDEN Slot-Start-Zeiten
  // für einen Trainer an einem Datum, basierend auf seinem `slot_duration`
  // und seinen Verfügbarkeitsfenstern. Slots werden am Fenster-Start
  // verankert (nicht starr an :00/:30). Beispiel: Verfügbarkeit 17:45-22:00
  // bei 30-Min-Slots → ["17:45", "18:15", "18:45", "19:15", ...].
  // Ausnahmen (Trainer-Exceptions) werden auf 15-Min-Raster zurückgesetzt,
  // damit Admin sie flexibel öffnen kann.
  const getValidSlotStartsForTrainer = utilGetValidSlotStartsForTrainer;

  const isTrainerAvailable = (trainerData, timeSlot) => {
    // 06/2026: studioweite Sperre blockiert ALLE Trainer-Slots im Fenster.
    if (findStudioBlockForSlot(trainerData, timeSlot)) return false;
    return utilIsTrainerAvailable(trainerData, timeSlot, {
      currentDate,
      studioOpeningHours,
      trainerVacations,
      trainerBlocks,
      trainerExceptions,
      appointments,
      densityConfig,
    });
  };

  const isTrainerOnVacation = (trainerId) =>
    utilFindTrainerVacation(trainerId, currentDate, trainerVacations);

  const isSlotOpenedByException = (trainerData, timeSlot) =>
    utilIsSlotOpenedByException(trainerData, timeSlot, {
      currentDate,
      trainerBlocks,
      trainerExceptions,
    });

  // 06/2026 — Kalender-Markierung: Info, ob ein verfügbarer Slot aktuell
  // nur von Admin/Trainer buchbar ist (Datum abgelaufen / noch nicht aktiv /
  // für Kunden ausgeblendet). null = normal kundenbuchbar.
  const getAdminOnlyInfo = (trainerData, timeSlot) => {
    const dateStr = toLocalYMD(currentDate);
    const dayName = currentDate
      .toLocaleDateString('en-US', { weekday: 'long' })
      .toLowerCase();
    return utilGetAdminOnlySlotInfo(trainerData, dateStr, dayName, timeSlot);
  };

  const getAppointmentForSlot = (trainerId, timeSlot) =>
    utilGetAppointmentForSlot(trainerId, timeSlot, appointments, currentDate);

  const isFirstSlotOfAppointment = utilIsFirstSlotOfAppointment;

  const calculateAppointmentHeight = utilCalculateAppointmentHeight;

  return (
    <TooltipProvider>
    <div className="space-y-6" data-testid="appointments-page">
      {/* Drag Cursor Follower - Global overlay that follows the mouse */}
      <DragCursorFollower
        draggedAppointment={draggedAppointment}
        draggedParticipant={draggedParticipant}
        dragCursorPos={dragCursorPos}
      />

      {/* Mitarbeiter-Aufgaben Banner — zuoberst, über den Geburtstagen */}
      <StaffTasksBanner studioFilter={studioFilter} studios={studios} />

      {/* Birthdays Banner - Show above calendar */}
      <BirthdayBanner birthdays={birthdaysToday} />

      {/* Header — sticky unter der Top-Bar, damit beim Scrollen nur der
          Kalender läuft und Datum/Navigation/Buttons sichtbar bleiben */}
      <div
        className="sticky top-[60px] lg:top-[64px] z-20 bg-white -mx-4 lg:-mx-8 px-4 lg:px-8 pt-2 pb-3 border-b border-gray-200 shadow-sm"
        data-testid="calendar-sticky-header"
      >
        <CalendarHeader
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
          formattedDate={formatDate()}
          onNavigateDate={navigateDate}
          onOpenBreakDialog={() => setBreakDialogOpen(true)}
          onOpenRescheduleDay={() => setRescheduleDayOpen(true)}
          showAllTrainers={showAllTrainers}
          setShowAllTrainers={setShowAllTrainers}
          showCancelled={showCancelled}
          setShowCancelled={setShowCancelled}
          onManualBilling={handleManualBilling}
          billingInProgress={billingInProgress}
          lastFetchedAt={lastFetchedAt}
          onRefresh={fetchAppointments}
        />
      </div>

      {/* 06/2026: Eskalierte WhatsApp-Verschiebe-Anfragen */}
      <RescheduleEscalationsPanel
        refreshSignal={escalationRefresh}
        onOfferNewSlots={(apptId) => setWaRescheduleAppt(apptId)}
      />

      {/* Studio Closed Banner */}
      <StudioClosedBanner
        currentDate={currentDate}
        isStudioOpenOnDate={isStudioOpenOnDate}
      />

      {/* Iter 336: Verwaiste-Daten-Banner (Phantom-Trainer + unbekanntes Studio).
          Sicherstellt dass Admin Termine nicht „verliert" wenn ein Trainer
          deaktiviert oder ein Studio gelöscht wurde. */}
      {(() => {
        const phantoms = (trainers || []).filter(
          (t) => t?.availability?._phantom_column
        );
        const orphanStudios = orphanStudioAppointments || [];
        if (phantoms.length === 0 && orphanStudios.length === 0) return null;
        return (
          <div
            className="bg-amber-50 border border-amber-300 text-amber-900 rounded-md px-4 py-3 flex items-start gap-3"
            data-testid="orphan-trainer-banner"
          >
            <svg
              className="w-5 h-5 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="text-sm space-y-1">
              {phantoms.length > 0 && (
                <p>
                  <span className="font-medium">
                    {phantoms.length === 1
                      ? '1 verwaister Trainer-Termin'
                      : `${phantoms.length} verwaiste Trainer-Termine`}
                  </span>
                  {' — Trainer '}
                  <span className="font-mono">
                    {phantoms
                      .map(
                        (t) =>
                          `${t.trainer?.first_name || ''} ${
                            t.trainer?.last_name || ''
                          }`.trim() || t.trainer?.id?.slice(0, 8)
                      )
                      .join(', ')}
                  </span>{' '}
                  ist nicht aktiv, hat aber Termine an diesem Tag. Erscheint als
                  graue Spalte am Kalender-Ende.
                </p>
              )}
              {orphanStudios.length > 0 && (
                <p>
                  <span className="font-medium">
                    {orphanStudios.length === 1
                      ? '1 Termin mit unbekanntem Studio'
                      : `${orphanStudios.length} Termine mit unbekanntem Studio`}
                  </span>
                  {' — '}das Studio wurde wahrscheinlich gelöscht. Diese Termine
                  werden in jedem Studio-Filter angezeigt. Bitte Termin neu
                  zuordnen (Klick &rarr; Verschieben &rarr; Studio wählen).
                </p>
              )}
            </div>
          </div>
        );
      })()}

      {/* Nicht im Kalender sichtbare Termine — GANZ OBEN, damit der Admin sofort reagieren kann */}
      {hiddenDayIssues.length > 0 && (
        <div data-testid="hidden-appointments-banner" className="p-4 bg-red-50 border-2 border-red-300 rounded-lg shadow-sm">
          <p className="text-sm font-bold text-red-800 flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            {hiddenDayIssues.length} Termin{hiddenDayIssues.length > 1 ? 'e' : ''} an diesem Tag im Kalender NICHT sichtbar — bitte sofort zuweisen:
          </p>
          <div className="mt-2 space-y-1.5">
            {hiddenDayIssues.map((iss, i) => {
              const st = new Date(iss.appointment.start_time);
              const hhmm = `${String(st.getHours()).padStart(2, '0')}:${String(st.getMinutes()).padStart(2, '0')}`;
              return (
                <div key={iss.appointment.id} className="flex items-center justify-between text-sm bg-white rounded-md px-2.5 py-1.5 border border-red-200" data-testid={`hidden-appointment-${i}`}>
                  <span className="text-gray-800">
                    {hhmm} Uhr · {iss.appointment.service_name || 'Termin'} · {iss.customer_names.join(', ')}
                    <span className="text-red-700 text-xs ml-2">({iss.problem})</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <select
                      data-testid={`hidden-appointment-assign-${i}`}
                      className="text-xs border border-red-300 rounded-md px-1.5 py-1 bg-white"
                      defaultValue=""
                      onChange={async (ev) => {
                        const tid = ev.target.value;
                        if (!tid) return;
                        try {
                          await axios.put(`${API}/appointments/${iss.appointment.id}`, { trainer_id: tid });
                          toast.success('Trainer zugewiesen — Termin ist jetzt im Kalender sichtbar');
                          setHiddenDayIssues((prev) => prev.filter((x) => x.appointment.id !== iss.appointment.id));
                          fetchAppointments();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Zuweisen fehlgeschlagen');
                        }
                      }}
                    >
                      <option value="">Trainer zuweisen…</option>
                      {trainers.map((td) => (
                        <option key={td.trainer.id} value={td.trainer.id}>
                          {td.trainer.name || `${td.trainer.first_name || ''} ${td.trainer.last_name || ''}`.trim()}
                        </option>
                      ))}
                    </select>
                    <button
                      data-testid={`hidden-appointment-fix-${i}`}
                      onClick={() => openEditForAppointment(iss.appointment)}
                      className="text-xs font-medium px-2.5 py-1 rounded-md bg-red-600 text-white hover:bg-red-700"
                    >
                      Bearbeiten
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 06/2026 (Spezifikation): Ansicht umschalten — Raster vs. Tagesliste */}
      {view === 'day' && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit" data-testid="day-layout-toggle">
            <button
              data-testid="day-layout-raster"
              onClick={() => changeDayLayout('raster')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                dayLayout === 'raster' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Raster
            </button>
            <button
              data-testid="day-layout-liste"
              onClick={() => changeDayLayout('liste')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                dayLayout === 'liste' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Tagesliste
            </button>
          </div>

      {/* 06/2026 (Stage 2 Beauty): Umschalter Mitarbeiter-/Platz-Ansicht */}
      {view === 'day' && dayLayout === 'raster' && resources.length > 0 && (
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit" data-testid="calendar-mode-toggle">
          <button
            data-testid="calendar-mode-trainer"
            onClick={() => setCalendarMode('trainer')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              calendarMode === 'trainer' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Mitarbeiter
          </button>
          <button
            data-testid="calendar-mode-resource"
            onClick={() => setCalendarMode('resource')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              calendarMode === 'resource' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Plätze
          </button>
        </div>
      )}
        </div>
      )}

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
            </div>
          ) : view === 'month' ? (
            <MonthView
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              setView={setView}
              daysInMonth={getDaysInMonth()}
              appointmentsByDateString={appointmentsByDateString}
            />
          ) : view === 'week' ? (
            <WeekView
              weekDays={getWeekDays()}
              timeSlots={getTimeSlots()}
              appointmentCountsByIsoDate={appointmentCountsByIsoDate}
            />
          ) : dayLayout === 'liste' ? (
            <DayListView
              appointments={filteredAppointments}
              currentDate={currentDate}
              serviceMap={serviceMap}
              trainers={orderedFilteredTrainers}
              getAppointmentColor={getAppointmentColor}
              onAppointmentClick={(apt) => openEditForAppointment(apt)}
              onRightClick={handleRightClick}
              onQuickCheckIn={quickCheckIn}
            />
          ) : (
            <div className="overflow-x-auto relative">
              {/* Current Time Indicator Line - OUTSIDE table for correct positioning */}
              <CurrentTimeIndicator
                currentDate={currentDate}
                calendarStartHour={calendarStartHour}
                calendarEndHour={calendarEndHour}
                calendarStartMinutes={CALENDAR_START_MINUTES}
                slotHeight={slotHeight}
              />

              {calendarMode === 'resource' ? (
                <ResourceDayView
                  resources={resources}
                  appointments={appointments}
                  currentDate={currentDate}
                  timeSlots={getTimeSlots()}
                  slotHeight={slotHeight}
                  calendarStartMinutes={CALENDAR_START_MINUTES}
                  serviceMap={serviceMap}
                  getAppointmentColor={getAppointmentColor}
                  trainerNameById={Object.fromEntries(
                    (trainers || []).map((td) => [
                      td.trainer?.id,
                      td.trainer?.name ||
                        `${td.trainer?.first_name || ''} ${td.trainer?.last_name || ''}`.trim(),
                    ])
                  )}
                  onAppointmentClick={(apt) => openEditForAppointment(apt)}
                />
              ) : (
              /* Grid Table — komplett ausgelagert in DayViewGrid (Iter 332) */
              <DayViewGrid
                gridRef={calendarGridRef}
                orderedFilteredTrainers={orderedFilteredTrainers}
                timeSlots={getTimeSlots()}
                slotHeight={slotHeight}
                calendarStartMinutes={CALENDAR_START_MINUTES}
                currentDate={currentDate}
                studioFilter={studioFilter}
                movingAppointment={movingAppointment}
                trainerBlocks={trainerBlocks}
                studioBlocks={studioBlocks}
                onBlockClick={(block, type) => setEditingBlock({ block, type })}
                onMoveIntoAppointment={async (targetApt) => {
                  // 08/2026: Verschiebung direkt in bestehenden 1:2/1:3-Termin einbuchen
                  const src = movingAppointment;
                  if (!src) return;
                  if (src.id === targetApt.id) return;
                  if (src.service_id !== targetApt.service_id) {
                    toast.error('Einbuchen nur in Termine mit dem gleichen Service möglich');
                    return;
                  }
                  const ids = src.only_customer_id
                    ? [src.only_customer_id]
                    : (() => {
                        const l = [...(src.customer_ids || [])].filter(Boolean);
                        if (src.customer_id && !l.includes(src.customer_id)) l.unshift(src.customer_id);
                        return l;
                      })();
                  try {
                    for (const cid of ids) {
                      await axios.post(`${API}/appointments/${src.id}/move-participant`, {
                        customer_id: cid,
                        target_trainer_id: targetApt.trainer_id,
                        target_start_time: targetApt.start_time,
                        target_end_time: targetApt.end_time,
                        target_appointment_id: targetApt.id,
                      });
                    }
                    toast.success(
                      src.only_customer_id
                        ? `${src.only_customer_name || 'Teilnehmer'} in den bestehenden Termin eingebucht`
                        : 'In den bestehenden Termin eingebucht'
                    );
                    setMovingAppointment(null);
                    fetchAppointments();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Einbuchen fehlgeschlagen');
                  }
                }}
                appointmentsByTrainerForDate={appointmentsByTrainerForDate}
                serviceMap={serviceMap}
                services={services}
                customers={customers}
                mindbodySlotsByTrainer={mindbodySlotsByTrainer}
                anamneseStatus={anamneseStatus}
                minServiceTotalDuration={minServiceTotalDuration}
                isTrainerAvailable={isTrainerAvailable}
                isSlotOpenedByException={isSlotOpenedByException}
                getAdminOnlyInfo={getAdminOnlyInfo}
                isTrainerOnVacation={isTrainerOnVacation}
                getMaxGapMinutes={getMaxGapMinutes}
                getAppointmentColor={getAppointmentColor}
                findMindbodyMappedSlotAt={findMindbodyMappedSlotAt}
                draggingTrainerId={draggingTrainerId}
                setDraggingTrainerId={setDraggingTrainerId}
                dragOverTrainerId={dragOverTrainerId}
                setDragOverTrainerId={setDragOverTrainerId}
                handleTrainerColumnDrop={handleTrainerColumnDrop}
                handleSlotClick={handleSlotClick}
                handleAvailableSlotClick={handleAvailableSlotClick}
                handleUnavailableSlotClick={handleUnavailableSlotClick}
                handleMoveToSlot={handleMoveToSlot}
                handleRightClick={handleRightClick}
                draggedAppointment={draggedAppointment}
                draggedParticipant={draggedParticipant}
                setDraggedParticipant={setDraggedParticipant}
                dropTarget={dropTarget}
                setDropTarget={setDropTarget}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                handleParticipantDragStart={handleParticipantDragStart}
                handleDragEnd={handleDragEnd}
                setSelectedSlot={setSelectedSlot}
                setSelectedTrainer={setSelectedTrainer}
                setBookingData={setBookingData}
                setCustomTime={setCustomTime}
                setIsAddingParticipant={setIsAddingParticipant}
                setAvailableServices={setAvailableServices}
                setBookingDialogOpen={setBookingDialogOpen}
                setAppointments={setAppointments}
                fetchAppointments={fetchAppointments}
                fetchServices={fetchServices}
                fetchCustomers={fetchCustomers}
                fetchAvailableServices={fetchAvailableServices}
                densityConfig={densityConfig}
                appointments={appointments}
                getSlotBlockReason={getSlotBlockReason}
              />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drag & Drop Preview - Small indicator at bottom */}
      {draggedAppointment && dropTarget && (
        <div className="fixed bottom-4 right-4 z-50 bg-teal-500 text-white rounded-lg shadow-xl px-3 py-2 text-sm font-medium">
          → {dropTarget.timeSlot} bei {dropTarget.trainer?.trainer?.first_name}
        </div>
      )}

      {/* Legend */}
      <CalendarLegend paymentColors={paymentColors} />

      {/* 06/2026: Sperre bearbeiten (Grund ändern / aufheben) */}
      <BlockEditDialog
        open={!!editingBlock}
        block={editingBlock?.block}
        type={editingBlock?.type}
        onClose={() => setEditingBlock(null)}
        onSaved={async () => {
          if (fetchVacationsAndBlocks) await fetchVacationsAndBlocks();
          if (fetchStudioBlocks) await fetchStudioBlocks();
        }}
      />

      {/* Dialoge — Phase 6 (Iter 334) ausgelagert in <AppointmentsDialogs> */}
      <AppointmentsDialogs
        contextMenu={contextMenu}
        setContextMenu={setContextMenu}
        selectedAppointment={selectedAppointment}
        services={services}
        trainers={trainers}
        anamneseStatus={anamneseStatus}
        onEditAppointment={handleEditAppointment}
        onViewAnamnese={handleViewAnamnese}
        onCheckIn={handleCheckIn}
        onMoveAppointment={handleMoveAppointment}
        onMoveParticipant={handleMoveParticipant}
        onRemoveParticipant={handleRemoveParticipant}
        onAvailableSlotClick={handleAvailableSlotClick}
        setDeleteOptionsDialogOpen={setDeleteOptionsDialogOpen}
        onManageSeries={(apt) => setManageSeriesId(apt.series_id)}
        onWhatsAppReschedule={(apptId) => setWaRescheduleAppt(apptId)}
        menuConfig={menuConfig}
        editDialogOpen={editDialogOpen}
        setEditDialogOpen={setEditDialogOpen}
        customers={customers}
        customTime={customTime}
        setCustomTime={setCustomTime}
        bookingData={bookingData}
        setBookingData={setBookingData}
        onEditSubmit={handleEditSubmit}
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        onConfirmDelete={confirmDelete}
        deleteOptionsDialogOpen={deleteOptionsDialogOpen}
        onDeleteAppointment={handleDeleteAppointment}
        bookingDialogOpen={bookingDialogOpen}
        setBookingDialogOpen={setBookingDialogOpen}
        setIsAddingParticipant={setIsAddingParticipant}
        setAvailableServices={setAvailableServices}
        selectedTrainer={selectedTrainer}
        selectedSlot={selectedSlot}
        currentDate={currentDate}
        availableServices={availableServices}
        availableServicesInfo={availableServicesInfo}
        isAddingParticipant={isAddingParticipant}
        isRecurring={isRecurring}
        setIsRecurring={setIsRecurring}
        recurringFrequency={recurringFrequency}
        setRecurringFrequency={setRecurringFrequency}
        recurringOccurrences={recurringOccurrences}
        setRecurringOccurrences={setRecurringOccurrences}
        notifyCustomer={notifyCustomer}
        setNotifyCustomer={setNotifyCustomer}
        isBooking={isBooking}
        onBookingSubmit={handleBookingSubmit}
        overwriteWarningDialogOpen={overwriteWarningDialogOpen}
        setOverwriteWarningDialogOpen={setOverwriteWarningDialogOpen}
        conflictingDates={conflictingDates}
        setConflictingDates={setConflictingDates}
        setPendingBookingData={setPendingBookingData}
        onConfirmBookingWithConflicts={confirmBookingWithConflicts}
        availabilityDialogOpen={availabilityDialogOpen}
        setAvailabilityDialogOpen={setAvailabilityDialogOpen}
        availabilitySlot={availabilitySlot}
        onAddBlock={handleAddBlock}
        onAddException={handleAddException}
        onRemoveBlock={handleRemoveBlock}
        onRemoveBlockException={handleRemoveBlockException}
        onAddBlockAllDay={handleAddBlockAllDay}
        onAddStudioBlock={handleAddStudioBlock}
        onRemoveStudioBlock={handleRemoveStudioBlock}
        quickCheckoutOpen={quickCheckoutOpen}
        setQuickCheckoutOpen={setQuickCheckoutOpen}
        checkoutCustomer={checkoutCustomer}
        onQuickCheckoutSuccess={handleQuickCheckoutSuccess}
        anamneseDialogOpen={anamneseDialogOpen}
        setAnamneseDialogOpen={setAnamneseDialogOpen}
        anamneseCustomer={anamneseCustomer}
        appointments={appointments}
        fetchAnamneseStatus={fetchAnamneseStatus}
        breakDialogOpen={breakDialogOpen}
        setBreakDialogOpen={setBreakDialogOpen}
        fetchAppointments={fetchAppointments}
        seriesSkippedInfo={seriesSkippedInfo}
        setSeriesSkippedInfo={setSeriesSkippedInfo}
      />

      {/* Serien-Verwaltung (08/2026) — via Rechtsklick "Serie verwalten" */}
      <SeriesManageDialog
        open={!!manageSeriesId}
        onOpenChange={(o) => !o && setManageSeriesId(null)}
        seriesId={manageSeriesId}
        onChanged={fetchAppointments}
      />

      {/* Tag verschieben (08/2026) */}
      <RescheduleDayDialog
        open={rescheduleDayOpen}
        onClose={() => setRescheduleDayOpen(false)}
        currentDate={currentDate}
        fetchAppointments={fetchAppointments}
      />

      {/* Termin per WhatsApp verschieben (06/2026, Task C) */}
      <WhatsAppRescheduleDialog
        open={!!waRescheduleAppt}
        appointmentId={waRescheduleAppt}
        onClose={() => setWaRescheduleAppt(null)}
        onSent={() => setEscalationRefresh((k) => k + 1)}
      />

      <SeriesSyncProgress seriesId={seriesSyncId} onDone={() => setSeriesSyncId(null)} />

      <PhysioCheckinDialog
        state={physioCheckin}
        onClose={() => setPhysioCheckin({ open: false, customerId: null, data: null })}
      />


    </div>
    </TooltipProvider>
  );
};

export default AppointmentsPage;
